import { useState } from "react";
import axios from "axios";
import { AlertTriangle, RotateCcw, ShieldCheck } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const RESET_PHRASE = "RESET LEARNER";

function detailText(error) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail) return JSON.stringify(detail);
  return error?.message || "The reset request failed.";
}

export default function TestLearnerResetCard() {
  const [email, setEmail] = useState("");
  const [preview, setPreview] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [confirmation, setConfirmation] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const runDryRun = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    setReceipt(null);
    setPreview(null);
    setConfirmation("");
    try {
      const { data } = await axios.post(
        `${API}/api/admin/test-reset/learner`,
        { email: email.trim(), confirmation: RESET_PHRASE, dry_run: true },
        { withCredentials: true },
      );
      setPreview(data);
    } catch (err) {
      setError(detailText(err));
    } finally {
      setBusy(false);
    }
  };

  const executeReset = async () => {
    if (!preview?.dry_run || confirmation !== RESET_PHRASE) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(
        `${API}/api/admin/test-reset/learner`,
        { email: preview.target_email, confirmation: RESET_PHRASE, dry_run: false },
        { withCredentials: true },
      );
      setReceipt(data);
      setPreview(null);
      setConfirmation("");
    } catch (err) {
      setError(detailText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      data-testid="admin-test-learner-reset"
      className="mt-6 rounded-2xl border border-amber-300 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950/30"
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5 rounded-lg bg-amber-100 p-2 dark:bg-amber-900/40">
          <RotateCcw className="h-4 w-4 text-amber-700 dark:text-amber-300" />
        </div>
        <div>
          <h2 className="font-bold text-slate-900 dark:text-white">Reset a test learner account</h2>
          <p className="mt-1 text-sm text-slate-700 dark:text-slate-300">
            Admin-only recovery. The server accepts only allowlisted test accounts and refuses
            accounts with protected payment records. Login security is unchanged.
          </p>
        </div>
      </div>

      <form onSubmit={runDryRun} className="mt-4 space-y-3">
        <label className="block text-sm font-semibold text-slate-800 dark:text-slate-200" htmlFor="test-learner-email">
          Learner account email
        </label>
        <input
          id="test-learner-email"
          data-testid="test-learner-reset-email"
          type="email"
          autoComplete="off"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          className="min-h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
          placeholder="test learner email"
        />
        <button
          type="submit"
          data-testid="test-learner-reset-preview"
          disabled={busy || !email.trim()}
          className="min-h-11 rounded-lg bg-slate-900 px-4 text-sm font-bold text-white disabled:opacity-50 dark:bg-slate-100 dark:text-slate-900"
        >
          {busy ? "Working…" : "Preview reset"}
        </button>
      </form>

      {preview && (
        <div data-testid="test-learner-reset-preview-result" className="mt-4 rounded-xl border border-amber-300 bg-white p-3 text-sm dark:border-amber-800 dark:bg-slate-900">
          <p className="font-bold text-slate-900 dark:text-white">Dry run only — nothing deleted</p>
          <p className="mt-1 text-slate-700 dark:text-slate-300">Target: {preview.target_email}</p>
          <p className="text-slate-700 dark:text-slate-300">Account ID: {preview.target_user_id}</p>
          <p className="mt-2 font-semibold text-slate-800 dark:text-slate-200">
            Linked records to remove: {preview.cascade_total}
          </p>
          {Object.keys(preview.cascade_inventory || {}).length > 0 ? (
            <ul className="mt-1 list-disc pl-5 text-slate-700 dark:text-slate-300">
              {Object.entries(preview.cascade_inventory).map(([collection, count]) => (
                <li key={collection}>{collection}: {count}</li>
              ))}
            </ul>
          ) : (
            <p className="mt-1 text-slate-600 dark:text-slate-400">No linked records in the reset cascade.</p>
          )}
          <div className="mt-3 flex items-start gap-2 text-amber-900 dark:text-amber-200">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <p>The account and listed records are permanently deleted. Enter RESET LEARNER below to execute.</p>
          </div>
          <label className="mt-3 block text-sm font-semibold text-slate-800 dark:text-slate-200" htmlFor="test-learner-reset-confirm">
            Type RESET LEARNER to confirm
          </label>
          <input
            id="test-learner-reset-confirm"
            data-testid="test-learner-reset-confirm"
            value={confirmation}
            onChange={(event) => setConfirmation(event.target.value)}
            className="mt-1 min-h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
          />
          <button
            type="button"
            data-testid="test-learner-reset-execute"
            disabled={busy || confirmation !== RESET_PHRASE}
            onClick={executeReset}
            className="mt-3 min-h-11 rounded-lg bg-red-700 px-4 text-sm font-bold text-white disabled:opacity-50"
          >
            {busy ? "Resetting…" : "Permanently reset test learner"}
          </button>
        </div>
      )}

      {receipt && (
        <div data-testid="test-learner-reset-receipt" role="status" className="mt-4 rounded-xl border border-emerald-300 bg-white p-3 text-sm text-slate-800 dark:border-emerald-800 dark:bg-slate-900 dark:text-slate-200">
          <p className="flex items-center gap-2 font-bold"><ShieldCheck className="h-4 w-4 text-emerald-600" />Reset completed</p>
          <p className="mt-1">Account: {receipt.target_email}</p>
          <p>Deleted account rows: {receipt.users_deleted}</p>
          <p>Deleted linked records: {receipt.cascade_total}</p>
        </div>
      )}

      {error && (
        <p data-testid="test-learner-reset-error" role="alert" className="mt-3 rounded-lg bg-red-100 p-3 text-sm text-red-900 dark:bg-red-950 dark:text-red-200">
          {error}
        </p>
      )}
    </section>
  );
}
